<template>
  <div class="care-homes-help__slider">
        <a href="#" class="care-homes-help__back" v-if="slideNo > 1" @click.prevent="goBack">Back</a>
        <div class="care-homes-help__slider-item" v-if="slideNo === 1">
          <h2 class="text-center mt-0">Has the person going into care completed a needs assessment?</h2>

          <div class="radio-container radio-container--type justify-content-center">
            <div
              v-for="option in slide1Options"
              class="radio-group radio-group--type mb-8"
              :class="{ 'radio-group--active': needAssessment === option.value }"
              @click="firstOptionSubmit(option.value)">
              <input
                type="radio"
                :id="makeIdValue(option.name)"
                name="needAssessment"
                :value="option.value"
                v-model="needAssessment">
              <label :for="makeIdValue(option.name)" :class="{ 'active': needAssessment === option.value }">
                <i :class="getIconClassArray(option.icon, option.name, 'rooms' )"></i>
                <span v-html="option.name"></span>
              </label>
            </div>
          </div>
        </div>

        <div class="care-homes-help__slider-item text-center" v-if="slideNo === 2">
          <h2 class="text-center mb-10">What is a needs assessment?</h2>
          <p class="mt-4 mb-10 text-center ml-auto mr-auto font-weight-light-bold lh1-5" style="max-width: 515px">
            A needs assessment accurately assesses what type of care a person needs. For example, rest home level care,
            hospital level care, dementia level care, or other types of specialist care. All needs assessments are
            performed by a qualified DHB-approved assessor. The suggested outcome of a needs assessment may be to move
            into a rest home.
          </p>
          <a
            href="/resource-centre/care-home-resources/what-is-a-needs-assessment"
            target="_blank"
            class="btn btn--green btn--learn-more ml-auto mr-auto mb-8"
          >
            Learn more
          </a>
          <a
            @click.prevent="submitSlide(3, 'no', '')"
            href="#"
            class="link-green font-weight-bold"
          >
            Continue to find Rest Homes
            <i class="icon-chev-right-green ml-1"></i>
          </a>

        </div>

        <div class="care-homes-help__slider-item text-center" v-if="slideNo === 3">
          <h2 class="text-center">What level of care is required?</h2>

          <div class="radio-container radio-container--type">
            <div
              v-for="option in slide3Options"
              class="radio-group radio-group--type radio-group--4-col"
              :class="{ 'radio-group--active': levelOfCare === option.value }"
              @click="submitSlide(4, 'levelOfCare', option.value)">
              <input
                type="radio"
                :id="makeIdValue(option.name)"
                name="levelOfCare"
                :value="option.value"
                v-model="levelOfCare">
              <label :for="makeIdValue(option.name)" :class="{ 'active': levelOfCare === option.value }">
                <i :class="getLevelOfCareIconClass(option.icon, option.value, 'levelOfCare' )"></i>
                <span v-html="option.name"></span>
              </label>
            </div>
          </div>
        </div>

        <div class="care-homes-help__slider-item text-center" v-if="slideNo === 4">
          <h2 class="text-center">How soon is care needed?</h2>

          <div class="radio-container radio-container--type">
            <div
              v-for="option in slide4Options"
              class="radio-group radio-group--type radio-group--4-col"
              :class="{ 'radio-group--active': timeFrame === option.name }"
              @click="submitSlide(5, 'timeFrame', option.name)">
              <input type="radio" :id="makeIdValue(option.name)" name="timeFrame" :value="option.name" v-model="timeFrame">
              <label :for="makeIdValue(option.name)" :class="{ 'active': timeFrame === option.name }">
                <i :class="getLevelOfCareIconClass(option.icon, option.name, 'timeFrame' )"></i>
                <span v-html="option.name"></span>
              </label>
            </div>
          </div>
        </div>

        <div class="care-homes-help__slider-item text-center" v-if="slideNo === 5">
          <h2 class="text-center mb-3">Do you have a room preference?</h2>
          <p class="mb-10">You can choose more than one room type</p>

          <div class="radio-container radio-container--type radio-container--loc radio-group--4-col pt-5">
            <div
              v-for="option in slide5Options"
              class="radio-group radio-group--type"
              :class="{ 'radio-group--active': isCheckboxChecked(option.name, 'rooms') }"
              @click="clickCheckBoxLabel"
            >
              <input
                type="checkbox"
                class="checkbox"
                :id="makeIdValue(option.name)"
                name="rooms[]"
                :value="option.name"
                v-model="data.rooms"
              >
              <label :for="makeIdValue(option.name)" :class="{ 'active': isCheckboxChecked(option.name, 'rooms') }">
                <i :class="getIconClassArray(option.icon, option.name, 'rooms' )"></i>
                <span>{{ option.name}}</span>
                <i class="icon icon--help-bg-green" v-if="option.help" v-tooltip.top-center="{content: option.help, trigger: 'hover'}"></i>
              </label>
            </div>
          </div>
          <button
            name="next"
            class="btn btn--green btn--next ml-auto mr-auto"
            :disabled="data.rooms.length === 0"
            @click.prevent="nextSlide(6)"
          >
            Next step
          </button>
        </div>

        <div class="care-homes-help__slider-item text-center" v-show="slideNo === 6">
          <h2 class="text-center">Where are you looking?</h2>
          <ul class="care-homes__filter-list care-homes__filter-list--slider">
            <li class="care-homes__filter-item mb-3">
              <region-dropdown
                id="region_list"
                class="dropdown--village dropdown--full-width"
                :empty-string="'Select region'"
                icon-class="icon--arrow-down"
                :extra-css-class="'dropdown--white'"
              ></region-dropdown>
            </li>
            <li class="care-homes__filter-item mb-3">
              <district-dropdown
                id="district_list"
                class="dropdown--village dropdown--full-width"
                :empty-string="'All Districts'"
                icon-class="icon--arrow-down"
                :districts="[]"
                :extra-css-class="'dropdown--white'"
                :default-type="'careHome'"
              ></district-dropdown>
            </li>
            <li class="care-homes__filter-item mb-3">
              <select-checkbox
                icon-class="icon--arrow-down"
                class="dropdown--village dropdown--full-width"
                :empty-string="'Select suburbs'"
              >
              </select-checkbox>
            </li>
            <li class="care-homes__filter-item">
              <button name="next" class="btn btn--green w-100" @click.prevent="submitSlide(7, '', '')">Next step</button>
            </li>
          </ul>
        </div>

        <div class="care-homes-help__slider-item text-center" v-if="slideNo === 7">
          <h2 class="text-center mb-10">What happens next?</h2>
          <p class="mt-4 mb-10 text-center ml-auto mr-auto font-weight-light-bold lh1-5" style="max-width: 515px">
            Our team will email you a list of suggested rest homes that meet your needs. From here, we can request
            information from managers on your behalf or you can make contact directly.
          </p>
          <a
            href="#"
            @click.prevent="submitSlide(8, '', '')"
            target="_blank"
            class="btn btn--green btn--learn-more ml-auto mr-auto mb-8"
          >
            Next step
          </a>
        </div>

        <div class="care-homes-help__slider-item text-center" v-if="slideNo === 8">
          <h2 class="text-center">Fill in your details</h2>
          <Form
            method="POST"
            class="ml-auto mr-auto"
            :validation-schema="schema"
            v-slot="{ errors }"
            @submit="submitRequest"
            id="careHomeHelp"
          >
            <div class="form-group form-group--input">
              <label for="name" class="sr-only">Name</label>
              <Field
                id="name"
                v-model="inputName"
                type="text"
                placeholder="Your name ..."
                class="form-control  form-control--bg-white"
                :class="{'is-danger': errors['name'] }"
                name="name"
              />
              <ErrorMessage name="name" class="is-danger mb-4 text-left" />
            </div>
            <div class="form-group form-group--input">
              <label for="email" class="sr-only">Email</label>
              <Field
                id="email"
                v-model="inputEmail"
                type="email"
                placeholder="Your email address ..."
                :class="{'is-danger': errors['email'] }"
                class="form-control form-control--bg-white"
                name="email"
              />
              <ErrorMessage name="email" class="is-danger mb-4 text-left" />
            </div>
            <div class="form-group form-group--input">
              <label for="phone_number" class="sr-only">Phone</label>
              <Field
                id="phone_number"
                type="text"
                v-model="inputPhone"
                placeholder="Your contact phone number ..."
                class="form-control form-control--bg-white"
                :class="{'is-danger': errors['phone_number'] }"
                name="phone_number"
              />
              <ErrorMessage name="phone_number" class="is-danger text-left" />
            </div>
            <div class="form-group form-group--input">
              <label for="message" class="sr-only">Note</label>
              <textarea
                id="message"
                v-model="inputMessage"
                placeholder="Any notes you'd like to add ..."
                rows="5"
                class="form-control form-control--bg-white"
                name="message"
                autocomplete="off"
              ></textarea>
            </div>
            <transition name="fade">
              <div :class="confirmation.class" class="message" v-if="confirmation.show">{{ confirmation.message }}</div>
            </transition>
            <div class="form-group mt-5">
              <input type="hidden" name="captcha_action" value="care_home_help_form">
              <button type="submit" class="btn btn--green btn--auto-width btn--send submit fs-16">
                Submit request
              </button>
            </div>
          </Form>
        </div>

  </div>
</template>

<script setup>
import {reactive, ref} from 'vue';
import {ErrorMessage, Field, Form} from 'vee-validate'
import axios from 'axios';
import regionDropdown from '../ListingPages/RegionDropdown.vue'
import districtDropdown from '../ListingPages/DistrictDropdown.vue'
import selectCheckbox from '../Global/SelectCheckbox.vue'
import {useFormSubmit} from '../../composables/useFormSubmit'

const { showServerSideValidateErrors } = useFormSubmit()

const schema = {
  name: 'required',
  email: 'required|email',
}

const needAssessment = ref('');
const timeFrame = ref('');
const levelOfCare = ref('');
const inputName = ref('');
const inputEmail = ref('');
const inputPhone = ref('');
const inputMessage = ref('');
const slideNo = ref(1);
const region = ref(0);
const district = ref(0);
const suburbs = ref([]);
const suburbSelectAll = ref(false);
const confirmation = reactive({show: false, message: '', class: ''})
let isSubmitting = ref(false)

const data = reactive({
  rooms: [],
});

const slide1Options = ref([
  {
    'name': 'Yes',
    'value': 'Yes',
    'icon': 'icon-clipboard-tick',
  },
  {
    'name': 'The process is underway',
    'value': 'The process is underway',
    'icon': 'icon-clipboard-lines',
  },
  {
    'name': 'Not yet',
    'value': 'Not yet',
    'icon': 'icon-clipboard-dots',
  }
]);

const slide2Options = ref([
  {
    'name': 'Immediately',
    'icon': 'icon-hourglass',
  },
  {
    'name': 'Within 2 weeks',
    'icon': 'icon-calendar',
  },
  {
    'name': 'Within 4 weeks',
    'icon': 'icon-calendar',
  },
  {
    'name': '4+ weeks',
    'icon': 'icon-calendar',
  }
]);

const slide3Options = ref([
  {
    'name': 'Rest Home Care',
    'value': 'Rest home care',
    'icon': 'icon-rest-home',
  },
  {
    'name': 'Hospital Care',
    'value': 'Hospital care',
    'icon': 'icon-hospital',
  },
  {
    'name': 'Dementia Care',
    'value': 'Dementia care',
    'icon': 'icon-dementia',
  },
  {
    'name': 'Palliative Care',
    'value': 'Palliative care',
    'icon': 'icon-palliative',
  },
  {
    'name': 'Psychogeriatric Care',
    'value': 'psychogeriatric care',
    'icon': 'icon-psychogeriatric',
  },
  {
    'name': 'Respite Care',
    'value': 'Respite care',
    'icon': 'icon-respite',
  },
  {
    'name': 'Day Care',
    'value': 'Day Care',
    'icon': 'icon-day-care',
  },
  {
    'name': 'Unsure',
    'value': 'Unsure',
    'icon': 'icon-unsure',
  }
]);

const slide4Options = ref([
  {
    'name': 'Immediately',
    'icon': 'icon-hourglass',
  },
  {
    'name': 'Within 2 weeks',
    'icon': 'icon-calendar',
  },
  {
    'name': 'Within 4 weeks',
    'icon': 'icon-calendar',
  },
  {
    'name': '4+ weeks',
    'icon': 'icon-calendar',
  },
]);

const slide5Options = ref([
  {
    'name': 'Standard room',
    'icon': 'icon-tick',
    'help': 'Government subsidies are available for standard rest home rooms. Every rest home is different, but standard rooms are usually single rooms. They tend to have space for a single bed and a few small items of personal furniture, such as an armchair and a television.'
  },
  {
    'name': 'Premium room',
    'icon': 'icon-tick',
    'help': 'A premium room is a room with additional features of a permanent or fixed nature. Common examples of premium features include an ensuite, more space, or garden access. Premium rooms will have an additional weekly charge. This additional cost is not funded by government subsidies.'
  },
  {
    'name': 'Care suite',
    'icon': 'icon-tick',
    'help': 'Care Suites commonly purchased under an Occupation Right Agreement (ORA), which means you pay an upfront capital sum to secure the room. A care suite is a much larger room and will most likely include an ensuite, kitchenette and lounge area.'
  },
  {
    'name': 'Serviced apartment',
    'icon': 'icon-tick',
    'help': 'Similar to care suites, a serviced apartment is purchased under an Occupation Right Agreement (ORA), which means you pay an upfront capital sum to secure the accommodation. Serviced apartments range in size from a studio apartment to two bedrooms. A serviced apartment will have its own ensuite, kitchenette and lounge area.'
  },
  {
    'name': 'Unsure or no preference',
    'icon': 'icon-tick',
  },
]);

const props = defineProps({
  regionList: {
    type: Array,
    default: () => [],
  },
});

const firstOptionSubmit = (value) => {
  needAssessment.value = value;
  if (value === 'Not yet') {
    slideNo.value = 2;
  } else {
    slideNo.value = 3;
  }
};

const submitSlide = (nextSlideNo, inputModalName, value = '') => {
  slideNo.value = nextSlideNo;
  if (value !== '') {
    data[inputModalName] = value;
  }
};

const isCheckboxChecked = (value, inputModalName) => {
  return (data[inputModalName].indexOf(value) !== -1);
};

const clickCheckBoxLabel = (e) => {
  // click label
  if (e.target.getElementsByClassName('checkbox')[0] !== undefined) {
    e.target.getElementsByClassName('checkbox')[0].click();
  }
};

const nextSlide = (nextSlideNo) => {
  slideNo.value = nextSlideNo;
};

const submitRequest = () => {
  isSubmitting.value = true
  confirmation.show = true
  confirmation.class = 'message--warning'
  confirmation.message = 'Submitting....'
  let formData = new FormData(document.getElementById('careHomeHelp'));
  window.grecaptcha.execute(document.getElementById('mySiteKeyVariable').value, {
    action: formData.get('captcha_action'),
  }).then((token) => {
    formData.set('g-recaptcha-response', token);
    axios.post('/care-home-help-submit',
      {
        needAssessment: needAssessment.value,
        timeFrame: timeFrame.value,
        levelOfCare: levelOfCare.value,
        rooms: data.rooms,
        name: inputName.value,
        email: inputEmail.value,
        phone: inputPhone.value,
        message: inputMessage.value,
        region: region.value,
        district: district.value,
        suburbs: suburbs.value,
        _token: document.head.querySelector("[name~=csrf-token][content]").content,
      }
    ).then(function (response) {
      confirmation.class = 'message--success'
      confirmation.message = response.data.msg

      setTimeout(function () {
        window.location.href = '/rest-homes';
      }, 2000);
    }).catch(error => {
      showServerSideValidateErrors(error, isSubmitting, confirmation)
    });
  });
};

const close = () => {
  window.location.href = '/rest-homes';
};

const makeIdValue = (value) => {
  return value.toLowerCase().replace(/ /g,"_");
};


const getLevelOfCareIconClass = (icon, name, inputModalName) => {
  const iconActive = (data[inputModalName] === name) ? `${icon}--active` : '';

  return `${icon} ${iconActive}`;
};

const getIconClassArray = (icon, name, inputModalName) => {
  const iconActive = (data[inputModalName].indexOf(name) !== -1) ? `${icon}--active` : '';

  return `${icon} ${iconActive}`;
};

const filterDistrictByRegion = (regionId) => {
  region.value = regionId;
  district.value = 0;
  suburbs.value = [];
  // EventHub.$emit('filterDistrictByRegion', regionId);
};

const filterSuburbsByDistrict = (districtId) => {
  district.value = districtId;
  suburbs.value = [];
  // EventHub.$emit('filterSuburbsByDistrict', districtId);
};

const updateSelectedSuburbs = (suburbsList) => {
  suburbs.value = suburbsList
};

const updateSelectedSuburb = (suburb) => {
  suburbs.value.push(suburb)
};

const updateSelectAll = (selectAll) => {
  suburbSelectAll.value = selectAll
};

const goBack = () => {
  if (slideNo.value === 3) {
    if(needAssessment.value === 'Not at this stage') {
      slideNo.value = 2
    } else {
      slideNo.value = 1
    }
  } else {
    slideNo.value -= 1
  }
}
</script>
